const { sumDiagnosisLineCost } = require("./bestekCosts");
const {
  BESTEK_LINE_REQUIREMENT,
  BESTEK_OUTCOME,
  BESTEK_RAMUTYPE,
  BESTEK_STATUS,
  BESTEK_TYPE,
  mapRamuTypeToLineRequirement,
} = require("../types/bestek.types");
const { requiresImmediatePayment } = require("../types/payments.types");

/**
 * Parse article number from FinERP RAMRPMDIMENSIESTRING
 * e.g. "1#SP012594A\u00052#Zwanenhals..."
 * Note: some lines store a BOM position ("1#Pos. 805") — not a real artnr.
 */
const parseArtNrFromDimensieString = (dimensieString) => {
  if (!dimensieString || typeof dimensieString !== "string") return "";
  const match = dimensieString.match(/1#([^\u0005]+)/);
  return match ? match[1].trim() : "";
};

/** FinERP sometimes puts "Pos. 6" / "Pos. 805" in dimensie segment 1 — not MMS Nummer. */
const isBomPositionLabel = (value) =>
  /^pos\.?\s*\d+/i.test(String(value || "").trim());

/**
 * Prefer art.artnr from herstramrepmat (RAMRPMARTNR); fall back to dimensie
 * segment 1 only when it looks like a real article number.
 */
const resolveRamrpmArtNr = (ram = {}) => {
  const fromArtJoin = String(ram.RAMRPMARTNR || "").trim();
  if (fromArtJoin && !isBomPositionLabel(fromArtJoin)) return fromArtJoin;

  const fromDimensie = parseArtNrFromDimensieString(ram.RAMRPMDIMENSIESTRING);
  if (fromDimensie && !isBomPositionLabel(fromDimensie)) return fromDimensie;

  return "";
};

const mapTakenToDiagnosisLine = (taken) => {
  const HTWERK = Number(taken.HTWERK) || 0;
  const HTKM = Number(taken.HTKM) || 0;
  const HTVERPL = Number(taken.HTVERPL) || 0;
  const HTTOTWERK = Number(taken.HTTOTWERK) || 0;
  const HTTOTKM = Number(taken.HTTOTKM) || 0;
  const HTTOTVERPL = Number(taken.HTTOTVERPL) || 0;
  let HTEHWERK = Number(taken.HTEHWERK) || 0;
  let HTEHPRKM = Number(taken.HTEHPRKM) || 0;
  let HTEHPRVERPL = Number(taken.HTEHPRVERPL) || 0;
  // Derive unit rates when FinERP only sent totals + quantities
  if (!HTEHWERK && HTWERK > 0 && HTTOTWERK > 0) {
    HTEHWERK = Math.round((HTTOTWERK / HTWERK) * 100) / 100;
  }
  if (!HTEHPRKM && HTKM > 0 && HTTOTKM > 0) {
    HTEHPRKM = Math.round((HTTOTKM / HTKM) * 100) / 100;
  }
  if (!HTEHPRVERPL && HTVERPL > 0 && HTTOTVERPL > 0) {
    HTEHPRVERPL = Math.round((HTTOTVERPL / HTVERPL) * 100) / 100;
  }

  const line = {
    sourceHTOBJ: taken.HTOBJ ?? null,
    description: taken.HTOMEXTERN || "",
    internalNote: taken.HTOMINTERN || "",
    technician: taken.HTUSER || "",
    startAt: taken.HTSTARTTIJD || null,
    stopAt: taken.HTSTOPTIJD || null,
    HTWERK,
    HTKM,
    HTVERPL,
    HTEHWERK,
    HTEHPRKM,
    HTEHPRVERPL,
    HTTOTWERK,
    HTTOTKM,
    HTTOTVERPL,
    HTTOTEXTRA: Number(taken.HTTOTEXTRA) || 0,
    HTBESTKK: Number(taken.HTBESTKK) || 0,
    HTTRANSK: Number(taken.HTTRANSK) || 0,
    HTEXTRAK: Number(taken.HTEXTRAK) || 0,
    HTLEVK: Number(taken.HTLEVK) || 0,
    HTKLK: Number(taken.HTKLK) || 0,
    lineTotal: 0,
  };
  line.lineTotal = sumDiagnosisLineCost(line);
  return line;
};

/**
 * Actual repair materials on the HRB (herstrepmat / rpm) at bestek creation
 * are diagnostic cost — later post-acceptance task RPM is out of scope here.
 */
const mapRpmToDiagnosisLine = (rpm = {}) => {
  const quantity = Number(rpm.RPMHOEV) || 0;
  const total = Number(rpm.RPMTOT) || 0;
  const artNr = String(rpm.RPMARTNR || "").trim();
  const tekst = String(rpm.RPMTEKST || "").trim() || "Diagnosemateriaal";
  const unit = String(rpm.RPMEH || "").trim();
  const parts = [tekst];
  if (artNr) parts.push(`(${artNr})`);
  if (quantity > 0) {
    parts.push(`× ${quantity}${unit ? ` ${unit}` : ""}`);
  }
  const line = {
    sourceHTOBJ: rpm.RPMOBJ ?? null,
    // herstrepmat.hersttaak_obj: the task this material was booked on.
    sourceTaskHTOBJ: rpm.RPMTAAK ?? null,
    isMaterial: true,
    artNr,
    quantity,
    unit,
    unitPrice: Number(rpm.RPMPRIJS) || 0,
    description: parts.join(" "),
    internalNote: "Diagnosemateriaal (RPM / herstrepmat)",
    technician: "",
    startAt: null,
    stopAt: null,
    HTWERK: 0,
    HTKM: 0,
    HTVERPL: 0,
    HTEHWERK: 0,
    HTEHPRKM: 0,
    HTEHPRVERPL: 0,
    HTTOTWERK: 0,
    HTTOTKM: 0,
    HTTOTVERPL: 0,
    HTTOTEXTRA: total,
    HTBESTKK: 0,
    HTTRANSK: 0,
    HTEXTRAK: 0,
    HTLEVK: 0,
    HTKLK: 0,
    lineTotal: 0,
  };
  line.lineTotal = sumDiagnosisLineCost(line);
  return line;
};

const sameFinErpObj = (a, b) =>
  a != null &&
  b != null &&
  Number.isFinite(Number(a)) &&
  Math.abs(Number(a) - Number(b)) < 1e-6;

/**
 * Diagnosis lines in display order: each task followed by the materials
 * booked on it, then materials without a (known) task.
 */
const buildDiagnosisLines = (taken = [], rpm = []) => {
  const materials = rpm
    .filter((item) => Number(item.RPMTOT) > 0 || Number(item.RPMHOEV) > 0)
    .map(mapRpmToDiagnosisLine);
  const used = new Set();
  const lines = [];
  for (const task of taken) {
    const taskLine = mapTakenToDiagnosisLine(task);
    lines.push(taskLine);
    materials.forEach((mat, index) => {
      if (used.has(index)) return;
      if (!sameFinErpObj(mat.sourceTaskHTOBJ, taskLine.sourceHTOBJ)) return;
      used.add(index);
      lines.push(mat);
    });
  }
  materials.forEach((mat, index) => {
    if (used.has(index)) return;
    lines.push({ ...mat, sourceTaskHTOBJ: null });
  });
  return lines;
};

const mapRamtakenToLaborLine = (ram) => ({
  sourceRAMUOBJ: ram.RAMUOBJ ?? null,
  lineNr: ram.RAMULIJN ?? null,
  // Client-facing docs use RAMUINTERN only; RAMUEXTERN is internal service info.
  description: ram.RAMUINTERN || "",
  internalDescription: ram.RAMUEXTERN || "",
  technician: ram.RAMUUSER || "",
  timeSeconds: Number(ram.RAMUTIJD) || 0,
  unitPrice: Number(ram.RAMUPRIJS) || 0,
  total: Number(ram.RAMUTOT) || 0,
  type: ram.RAMUTYPE || "",
  lineRequirement: mapRamuTypeToLineRequirement(ram.RAMUTYPE),
});

const mapRamrpmToMaterialLine = (ram) => {
  const quantity = Number(ram.RAMRPMHOEV) || 0;
  const total = Number(ram.RAMRPMTOT) || 0;
  const unitPrice =
    quantity > 0 ? Math.round((total / quantity) * 100) / 100 : total;
  return {
    sourceLineNr: ram.RAMRPMLIJN ?? null,
    sourceRAMRPUOBJ: ram.RAMRPUOBJ ?? null,
    description: ram.RAMRPMTEKST || "",
    artNr: resolveRamrpmArtNr(ram),
    quantity,
    unit: ram.RAMRPMEH || "stk",
    unitPrice,
    total,
    stockTotal: null,
  };
};

const mapRamtakenToRaming = (ram, materialLines = []) => ({
  sourceRAMUOBJ: ram.RAMUOBJ ?? null,
  lineNr: ram.RAMULIJN ?? null,
  // Client-facing docs use RAMUINTERN only; RAMUEXTERN is internal service info.
  description: ram.RAMUINTERN || "",
  internalDescription: ram.RAMUEXTERN || "",
  technician: ram.RAMUUSER || "",
  timeSeconds: Number(ram.RAMUTIJD) || 0,
  unitPrice: Number(ram.RAMUPRIJS) || 0,
  total: Number(ram.RAMUTOT) || 0,
  type: ram.RAMUTYPE || "",
  lineRequirement: mapRamuTypeToLineRequirement(ram.RAMUTYPE),
  materialLines,
  newProductOptions: [],
});

/** Task booked on a supplier (herstt.lev_obj): the supplier's repair, not diagnosis. */
const isSupplierTask = (task = {}) => Boolean(String(task.HTLEVNAAM || "").trim());

/**
 * Supplier repairs have no FinERP raming: the supplier's price is entered as a
 * leverancierstaak. On the bestek it becomes a MOET raming at klantkost only
 * (never levkost: €0 is flagged at publish) and no longer counts as diagnose cost.
 */
const mapSupplierTaskToRaming = (task = {}) => {
  const supplier = String(task.HTLEVNAAM || "").trim();
  const extern = String(task.HTOMEXTERN || "").trim();
  const price = Number(task.HTKLK) || 0;
  return {
    sourceRAMUOBJ: null,
    sourceSupplierTaskHTOBJ: task.HTOBJ ?? null,
    isSupplierTask: true,
    // Task line numbers are not raming line numbers: keep them apart.
    lineNr: null,
    // Customer-facing: nothing from the supplier task (its name or notes are
    // for us and the supplier). Service sees them via technician/internal.
    description: "Herstelling",
    internalDescription: [extern, String(task.HTOMINTERN || "").trim()]
      .filter(Boolean)
      .join(" — "),
    technician: supplier,
    timeSeconds: 0,
    unitPrice: price,
    total: price,
    type: BESTEK_RAMUTYPE.MOET,
    lineRequirement: BESTEK_LINE_REQUIREMENT.MOET,
    materialLines: [],
    newProductOptions: [],
  };
};

/** Material booked on a task (rpm), as a repair material line. */
const mapRpmToRepairMaterialLine = (rpm = {}) => {
  const quantity = Number(rpm.RPMHOEV) || 0;
  const total = Number(rpm.RPMTOT) || 0;
  const unitPrice =
    Number(rpm.RPMPRIJS) ||
    (quantity > 0 ? Math.round((total / quantity) * 100) / 100 : total);
  return {
    sourceLineNr: null,
    sourceRAMRPUOBJ: null,
    description: String(rpm.RPMTEKST || "").trim() || "Materiaal",
    artNr: String(rpm.RPMARTNR || "").trim(),
    quantity,
    unit: String(rpm.RPMEH || "").trim() || "stk",
    unitPrice,
    total,
    stockTotal: null,
  };
};

/** Behandelingskost booked as an article (HER BEHAKO25, HER BEHAKO50, ...). */
const isBehandelkostRpm = (rpm = {}) =>
  /^HER\s*BEHAKO/i.test(String(rpm.RPMARTNR || "").trim());

/**
 * Bestek na herstel / administratief: the repair happened during diagnosis.
 * Task hours stay diagnose cost; every material booked on the tasks becomes
 * the repair, as one MOET raming (no fake ramingen needed).
 */
const buildExecutedRepairRaming = (rpm = []) => ({
  ...mapRamtakenToRaming({ RAMUINTERN: "Uitgevoerde herstelling", RAMUTYPE: BESTEK_RAMUTYPE.MOET }),
  isExecutedRepair: true,
  materialLines: rpm
    .filter((item) => Number(item.RPMTOT) > 0 || Number(item.RPMHOEV) > 0)
    .map(mapRpmToRepairMaterialLine),
});

/** Replacement article from herst.vervang_art_obj — only for the first moet raming.
 * Price is NOT taken from FinERP ecom_prijs (VERVANGARTPRIJS); the controller
 * fills unitPrice via CAC GetArticlesPriceAndStock (richtprijs onder voorbehoud).
 */
const mapVervangArtToProductOption = (hrb = {}) => {
  const artNr = String(hrb.vervangArtNr || hrb.VERVANGARTNR || "").trim();
  const description = String(
    hrb.vervangArtNaam || hrb.VERVANGARTNAAM || ""
  ).trim();
  const artObj = hrb.vervangArtObj ?? hrb.VERVANGARTOBJ ?? null;
  if (!artNr && !description && artObj == null) return null;
  return {
    description: description || artNr || "Vervangartikel",
    artNr,
    unitPrice: 0,
    unit: "stk",
    stockTotal: null,
    sourceVervangArtObj: artObj,
  };
};

/**
 * Carries the vervangartikel when FinERP has no raming at all (typical for an
 * onherstelbaar bestek). Optional, no cost, hidden in raming lists.
 */
const buildReplacementHostRaming = (product) => ({
  sourceRAMUOBJ: null,
  lineNr: null,
  description: "Vervangartikel",
  internalDescription: "",
  technician: "",
  timeSeconds: 0,
  unitPrice: 0,
  total: 0,
  type: "",
  lineRequirement: BESTEK_LINE_REQUIREMENT.KAN,
  isReplacementHost: true,
  materialLines: [],
  newProductOptions: [product],
});

const attachVervangArtToFirstMoetRaming = (repairRamingen = [], hrb = {}) => {
  const product = mapVervangArtToProductOption(hrb);
  if (!product) return repairRamingen;
  if (repairRamingen.length === 0) {
    return [buildReplacementHostRaming(product)];
  }

  // First moet raming; without one (onherstelbaar), the first raming.
  const moetIndex = repairRamingen.findIndex(
    (r) => r.lineRequirement === BESTEK_LINE_REQUIREMENT.MOET
  );
  const hostIndex = moetIndex >= 0 ? moetIndex : 0;

  return repairRamingen.map((raming, index) => {
    if (index === hostIndex) {
      return { ...raming, newProductOptions: [product] };
    }
    // Other ramingen never carry a replacement article
    return { ...raming, newProductOptions: [] };
  });
};

/**
 * Build a new bestek draft payload from an HRB / SQLLopendeHRB document.
 * Content is FinERP-sourced; portal does not edit these fields.
 */
const buildBestekFromHrb = (hrb, { bestekType = BESTEK_TYPE.STANDARD } = {}) => {
  if (!hrb) return null;
  const repaired =
    bestekType === BESTEK_TYPE.AFTER_REPAIR ||
    bestekType === BESTEK_TYPE.ADMINISTRATIVE;

  const customerStreet = hrb.ADLEVLIJN1 || hrb.ADLIJN1 || "";
  const customerZip = hrb.ADLEVPOSTC || hrb.ADPOSTC || "";
  const customerCity = hrb.ADLEVLOC || hrb.ADLOC || "";

  const allTaken = hrb.taken || [];
  const supplierTaken = allTaken.filter(isSupplierTask);
  const internalTaken = allTaken.filter((task) => !isSupplierTask(task));
  // After repair: materials are the repair, so only the hours (and a
  // behandelingskost article) stay diagnose.
  const allRpm = hrb.rpm || [];
  const repairRpm = repaired
    ? allRpm.filter((item) => !isBehandelkostRpm(item))
    : [];
  const diagnosisLines = repaired
    ? buildDiagnosisLines(internalTaken, allRpm.filter(isBehandelkostRpm))
    : buildDiagnosisLines(internalTaken, allRpm);
  // Already repaired: open FinERP ramingen are either the same work or work
  // that was not done, so they are dropped (user decision 29/10).
  const ramtaken = repaired ? [] : hrb.ramtaken || [];
  const ramrpm = repaired ? [] : hrb.ramrpm || [];
  const materialByRaming = ramrpm.reduce((acc, ram) => {
    const key = ram.RAMRPUOBJ != null ? String(ram.RAMRPUOBJ) : "__orphan__";
    if (!acc[key]) acc[key] = [];
    acc[key].push(mapRamrpmToMaterialLine(ram));
    return acc;
  }, {});
  let repairRamingen = ramtaken.map((ram) => {
    const key = ram.RAMUOBJ != null ? String(ram.RAMUOBJ) : null;
    return mapRamtakenToRaming(ram, key ? materialByRaming[key] || [] : []);
  });
  const linkedMaterialKeys = new Set(
    repairRamingen.flatMap((r) =>
      (r.materialLines || []).map(
        (m) => `${m.sourceRAMRPUOBJ}-${m.sourceLineNr}-${m.description}`
      )
    )
  );
  const orphanMaterials = ramrpm
    .map(mapRamrpmToMaterialLine)
    .filter((mat) => {
      const key = `${mat.sourceRAMRPUOBJ}-${mat.sourceLineNr}-${mat.description}`;
      if (linkedMaterialKeys.has(key)) return false;
      if (mat.sourceRAMRPUOBJ == null) return true;
      return !repairRamingen.some(
        (r) => Number(r.sourceRAMUOBJ) === Number(mat.sourceRAMRPUOBJ)
      );
    });
  for (const mat of orphanMaterials) {
    const byLineNr = repairRamingen.find(
      (r) => r.lineNr != null && Number(r.lineNr) === Number(mat.sourceLineNr)
    );
    if (byLineNr) {
      byLineNr.materialLines.push(mat);
    } else if (repairRamingen.length > 0) {
      repairRamingen[0].materialLines.push(mat);
    } else {
      repairRamingen.push(
        mapRamtakenToRaming(
          {
            RAMUINTERN: mat.description || "Materialen",
            RAMULIJN: mat.sourceLineNr,
            RAMUTYPE: "1MO",
          },
          [mat]
        )
      );
    }
  }

  repairRamingen.push(...supplierTaken.map(mapSupplierTaskToRaming));
  if (repaired) {
    repairRamingen.unshift(buildExecutedRepairRaming(repairRpm));
  }
  if (!repaired) {
    repairRamingen = attachVervangArtToFirstMoetRaming(repairRamingen, hrb);
  }
  // Webshop checkout uses the same Dias flag for a mandatory PO / referentie.
  const customerReferenceRequired =
    hrb.diasOnderneming?.fctrefRequired === true;

  let contactName = "";
  let contactEmail = "";
  let contactPhone = "";
  const diasContact = hrb.diasContact;
  if (diasContact) {
    contactName =
      [diasContact.Voornaam, diasContact.Naam].filter(Boolean).join(" ") ||
      diasContact.naam ||
      "";
    contactEmail = diasContact.eml || diasContact.Email || "";
    contactPhone = diasContact.Tf || diasContact.Tel || diasContact.phone || "";
    // Communications array (same shape as diasOnderneming)
    if (!contactEmail && Array.isArray(diasContact.Communications)) {
      const eml = diasContact.Communications.find(
        (c) => String(c.Type || "").toLowerCase() === "eml" && c.Value
      );
      if (eml?.Value) contactEmail = String(eml.Value).trim();
    }
  }

  // Fall back to company (onderneming) standard e-mail / phone / name
  const diasOnderneming = hrb.diasOnderneming;
  if (diasOnderneming) {
    if (!contactName) {
      contactName =
        diasOnderneming.DisplayAs ||
        diasOnderneming.LastName ||
        hrb.KLANAAM ||
        "";
    }
    if (!contactEmail && Array.isArray(diasOnderneming.Communications)) {
      const byType = (type) =>
        diasOnderneming.Communications.find(
          (c) =>
            String(c.Type || "").toLowerCase() === type &&
            c.Value &&
            String(c.Value).trim()
        );
      const eml =
        byType("eml") || byType("emlinv") || byType("ad_eml") || null;
      if (eml?.Value) contactEmail = String(eml.Value).trim();
    }
    if (!contactPhone && Array.isArray(diasOnderneming.Communications)) {
      const tf = diasOnderneming.Communications.find(
        (c) => String(c.Type || "").toLowerCase() === "tf" && c.Value
      );
      if (tf?.Value) contactPhone = String(tf.Value).trim();
    }
  }

  // No behandelingskost in FinERP: charge what the diagnosis actually cost
  // (agreed with service 26/10). A higher amount is still set in FinERP.
  const ramBehandelkost = Number(hrb.ramBehandelkost);
  const hasRamBehandelkost =
    Number.isFinite(ramBehandelkost) && ramBehandelkost > 0;
  const forfaitDiagnosticCost = hasRamBehandelkost
    ? ramBehandelkost
    : Math.round(
        diagnosisLines.reduce(
          (sum, line) => sum + sumDiagnosisLineCost(line),
          0
        ) * 100
      ) / 100;
  const forfaitDiagnosticCostSource = hasRamBehandelkost
    ? "finerp"
    : "diagnosis";

  const hasMoetRaming = repairRamingen.some(
    (r) => r.lineRequirement === BESTEK_LINE_REQUIREMENT.MOET
  );

  return {
    DOSSIERNR: hrb.DOSSIERNR,
    DOKNR: hrb.DOKNR,
    FICHE: hrb.FICHE,
    KLANTNR: hrb.KLANTNR,
    customerName: hrb.KLANAAM || "",
    customerStreet,
    customerZip,
    customerCity,
    contactName,
    contactEmail,
    contactPhone,
    machineName: hrb.TEKST || "",
    machineType: "",
    machineSerialNumber: "",
    machineCounter: "",
    reason: hrb.HERSTREDEN || "",
    isUnderWarranty: Boolean(hrb.GARANTIE),
    remarks: "",
    // Already repaired: herstelbaar, also without any FinERP raming.
    outcome:
      repaired || hasMoetRaming
        ? BESTEK_OUTCOME.REPAIRABLE
        : BESTEK_OUTCOME.UNREPAIRABLE,
    bestekType,
    diagnosisLines,
    repairRamingen,
    forfaitDiagnosticCost,
    forfaitDiagnosticCostSource,
    forfaitCategoryKey: null,
    forfaitCategoryLabel: "",
    allowBuyNewAndKeepOld: false,
    /** Same rule as LVB factureerbaar / OKTOFCT: BTLCONTANT or kontant/voorafbetaling. */
    requiresImmediatePayment: requiresImmediatePayment({
      BTLCONTANT: hrb.BTLCONTANT,
      BTLCOND: hrb.BTLCOND,
    }),
    customerReferenceRequired,
    status: BESTEK_STATUS.DRAFT,
    isSubmitted: false,
  };
};

module.exports = {
  parseArtNrFromDimensieString,
  isBomPositionLabel,
  resolveRamrpmArtNr,
  mapTakenToDiagnosisLine,
  mapRpmToDiagnosisLine,
  mapRamtakenToLaborLine,
  mapRamtakenToRaming,
  mapRamrpmToMaterialLine,
  mapVervangArtToProductOption,
  attachVervangArtToFirstMoetRaming,
  buildDiagnosisLines,
  isSupplierTask,
  mapSupplierTaskToRaming,
  mapRpmToRepairMaterialLine,
  buildExecutedRepairRaming,
  buildBestekFromHrb,
};
